// /invite#<token> (and the former /platform-invitation#<token>): accepting a RadioOS invitation.
// The token is read from the URL fragment and removed from the address bar at once
// (takeActivationToken), kept in memory only and sent only in POST bodies. The person chooses how
// to sign in: Microsoft or Google (the server binds the verified account - its e-mail must be the
// invited, verified address) or a RadioOS password (the one-time activation page follows, then
// the authenticator app when the role requires MFA).
import { useEffect, useState } from 'react';
import { takeActivationToken } from '../../services/activation';
import { acceptInvitation, inspectInvitation, type InvitationInfo, type SignInMethodType } from '../../services/identityPlatform';

export type InvitationStage =
  | { kind: 'checking' }
  | { kind: 'ready'; invitation: InvitationInfo; refused?: string }
  | { kind: 'redirecting'; method: SignInMethodType }
  | { kind: 'invalid' }
  | { kind: 'unavailable' };

export const INVALID_INVITATION = 'This invitation link is invalid, already used, cancelled or expired. Ask your administrator for a new invitation.';

const METHOD_TEXT: Record<SignInMethodType, { button: string; hint: string }> = {
  microsoft: { button: 'Continue with Microsoft', hint: 'Use the Microsoft work account of the invited address.' },
  google: { button: 'Continue with Google', hint: 'Use the Google account of the invited address (its e-mail must be verified by Google).' },
  radioos: { button: 'Create a RadioOS password', hint: 'Choose a password on the next page; an authenticator app follows when your role requires it.' },
};

function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function InvitationView({ stage, onAccept }: { stage: InvitationStage; onAccept: (m: SignInMethodType) => void }) {
  return (
    <main className="min-h-screen bg-gray-900 text-gray-100 flex items-center justify-center p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-md p-6 space-y-4">
        <h1 className="text-xl font-semibold">RadioOS invitation</h1>
        {stage.kind === 'checking' && <p role="status">Checking the invitation…</p>}
        {stage.kind === 'ready' && (
          <>
            <p>
              {stage.invitation.invited_by ? `${stage.invitation.invited_by} invited you` : 'You have been invited'} to RadioOS
              as <span className="font-mono">{stage.invitation.email}</span>.
            </p>
            {stage.invitation.roles.length > 0 && (
              <div>
                <p className="text-sm text-gray-400">Your access:</p>
                <ul className="list-disc ml-5 text-sm">{stage.invitation.roles.map((r) => <li key={r}>{r}</li>)}</ul>
              </div>
            )}
            <p className="text-sm text-gray-400">Valid until {when(stage.invitation.expires_at)}. Choose how you will sign in:</p>
            {stage.refused && <p role="alert" className="text-sm text-yellow-200">{stage.refused}</p>}
            <div className="space-y-2">
              {stage.invitation.methods.length === 0 && <p role="alert" className="text-yellow-200">No sign-in method is available right now. Please try again later.</p>}
              {stage.invitation.methods.map((m) => (
                <div key={m}>
                  <button className="w-full px-4 py-2 rounded bg-blue-600 hover:bg-blue-500 font-medium" onClick={() => onAccept(m)}>{METHOD_TEXT[m]?.button || m}</button>
                  <p className="text-xs text-gray-400 mt-1">{METHOD_TEXT[m]?.hint}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400">An account with a different e-mail address does not accept the invitation, and an account already linked to RadioOS cannot be used again.</p>
          </>
        )}
        {stage.kind === 'redirecting' && <p role="status">{stage.method === 'radioos' ? 'Opening the password page…' : 'Redirecting to the sign-in…'}</p>}
        {stage.kind === 'invalid' && <p role="alert" className="text-yellow-200">{INVALID_INVITATION}</p>}
        {stage.kind === 'unavailable' && <p role="alert" className="text-red-200">The service could not be reached. Please try again later.</p>}
      </div>
    </main>
  );
}

export default function InvitationPage() {
  const [token] = useState(() => takeActivationToken(window.location, window.history));
  const [stage, setStage] = useState<InvitationStage>(token ? { kind: 'checking' } : { kind: 'invalid' });
  useEffect(() => {
    if (!token) return;
    inspectInvitation(token).then((r) => setStage(r.kind === 'valid' ? { kind: 'ready', invitation: r.invitation } : r));
  }, [token]);
  const accept = async (method: SignInMethodType) => {
    const previous = stage;
    setStage({ kind: 'redirecting', method });
    const r = await acceptInvitation(token, method);
    if (r.kind === 'redirect') window.location.assign(r.url);
    else if (r.kind === 'refused' && previous.kind === 'ready') setStage({ ...previous, refused: r.message });
    else if (r.kind !== 'refused') setStage(r);
  };
  return <InvitationView stage={stage} onAccept={accept} />;
}
